import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import pick from 'lodash/pick';
import styled, { cx, StylesProps } from '../../lib/styles';
import { connect, useSelector } from 'react-redux';
import LazyLoad from 'react-lazyload';
import { compose, onlyUpdateForKeys, withProps, withState } from 'recompose';
import memoize from '../../lib/selectorUtils';
import { actions } from './store';
import {
  articleSelector,
  adConfigSelector,
  socialMediaSelector,
  faviconSelector,
  Brand,
  AdConfig,
  SocialMedia,
  adsSelector,
  suggestedArticlesUrlSelector,
  hideCommentsSelector,
  hideStickyRecircSelector,
  hideAdsSelector,
  showLinkbyPubFeedSelector,
} from './store/selectors';
import { Article } from '../../models/article';
import Spacer from '../../components/presentational/Spacer';
import PageBody from '../../components/presentational/PageBody';
import TypeformEmbed from '../../components/presentational/TypeformEmbed';
import Metadata from './Metadata';
import { callOnMount } from '../../behaviour/patterns';

import Ad, { Ads } from '../../components/presentational/Ad';
import AdSetup from '../../components/presentational/Ad/AdSetup';
import StandardArticleLayout from './StandardArticleLayout';
import SponsoredArticleLayout from './SponsoredArticleLayout';
import ArticleHolidays from './ArticleHolidays';
import articleStyles from './Article_styles';
import { pushEvent, raiseEvent, SIGNUP_ARTICLE_COLLECTION_CHALLENGE } from '../../lib/gtm';
import GameLayout from './GameLayout';
import { post } from '../../lib/api';
import endpoints from '../../lib/endpoints';
import { getArticleTargeting } from '../../lib/adTargeting';
import MembershipChallenge from '../../components/presentational/MembershipChallenge';
import { useLocalStorage } from '../../lib/hooks/useLocalStorage';
import dayjs from 'dayjs';
import { TrackingDataState } from '../../store/reducers/trackingData';
import { trackingDataSelector } from '../../store/selectors';
import theme from "../../lib/theme";

const { layoutWidths } = theme;
const HIDDEN_ADHESION = process.env.HIDDEN_ADHESION;

interface CommonArticleProps {
  brand: Brand;
}
export interface ArticleProps extends CommonArticleProps {
  article: Article;
  ads: Ads;
  socialMedia: SocialMedia;
  adConfig: AdConfig;
  faviconUrl: string;
  activeTypeform?: string;
  setActiveTypeform: (form: string) => any;
  suggestedArticlesUrl?: string;
  hideComments?: boolean;
  hideStickyRecirc?: boolean;
  hideAds?: boolean;
  showLinkbyPubFeed?: boolean;
  trackingProcessed?: boolean;
}

const isAMPEnabled = ({ link, seo_canonical_url }: Article) =>
  (seo_canonical_url || link).includes('startsat60.com/');

const Article = ({
  brand,
  article,
  socialMedia,
  ads,
  adConfig,
  faviconUrl,
  activeTypeform,
  hideComments,
  hideStickyRecirc,
  hideAds,
  showLinkbyPubFeed,
  styles,
}: ArticleProps & StylesProps<typeof articleStyles>) => {
  const targeting = getArticleTargeting(article);
  const trackingSelectorData: TrackingDataState = useSelector(trackingDataSelector);
  const [localStorageTracking, setLocalStorageTracking] = useLocalStorage('sas', null);
  const [localStorageTrackingObject, setLocalStorageTrackingObject] = useState({}); // Backup for cookie storage - TODO: remove 
  const [trackingHasBeenProcessed, setTrackingHasBeenProcessed] = useState(false);
  const buyoutIsExpired = !article.advertiserBuyoutExpiration || dayjs(article.advertiserBuyoutExpiration).isBefore(dayjs(), 'day');

  useEffect(() => {
    setLocalStorageTrackingObject(JSON.parse(localStorageTracking || '{}'));
  }, [localStorageTracking, setLocalStorageTrackingObject]);

  //  Debugging Only
  // useEffect(() => {
  //   console.log('[Debugging] Article useEffect', { 
  //     trackingSelectorData, 
  //     link: article?.url, 
  //     taxonomy: article.article_type, 
  //     id: article.id, 
  //     brand, 
  //     timestamp: dayjs().valueOf(),
  //     ads,
  //     adConfig,
  //   });
  // }, [trackingSelectorData]);

  useEffect(() => {
    //  Only post tracking once after waiting for state to be loaded
    if (trackingHasBeenProcessed || !trackingSelectorData.isLoaded) return;
    //  This should only run once per article view
    const historyTrackingData = {
      email: trackingSelectorData?.email,
      aggregator_url: article.link,
      aggregator_text: article.category,
      additional_data: {
        anonymous_uuid: trackingSelectorData.anonymous_uuid
      },
    };
    //  Post tracking data to API
    post(endpoints.userTracking, historyTrackingData)
    // .then(() => {
    //   console.log('[Article - should only run once] Tracking data posted successfully', { historyTrackingData });
    // })
    .catch((error) => {
      console.error('[Article - should only run once] Error posting tracking data', error);
    }).finally(() => setTrackingHasBeenProcessed(true));
  }, [trackingHasBeenProcessed, trackingSelectorData]);

  // TODO: This will eventually become redundant
  // Update localStorage with tracking history
  useEffect(() => {
    // Once state is loaded, update tracker
    if (!trackingSelectorData.isLoaded) return;
    const newHistoryItem = { 
      link: article?.url, 
      taxonomy: article.article_type, 
      id: article.id, 
      brand, 
      timestamp: dayjs().valueOf() 
    };

    const historyForStorage = {
      ...trackingSelectorData,
      ...localStorageTrackingObject,
      uuid: trackingSelectorData.anonymous_uuid,
      email: trackingSelectorData?.email,
      history: [newHistoryItem].concat(localStorageTrackingObject['history'] || []).slice(0, 5), // add new item and limit to 5 items
    };
    setLocalStorageTracking(JSON.stringify(historyForStorage));
  }, [trackingSelectorData]);

  return (
    <PageBody>
      <Helmet titleTemplate={`%s - ${socialMedia.site}`} title={article.title}>
        {(showLinkbyPubFeed && process.env.COOKIE_NAME === 'id_token') && (<script src={`//pubfeed.linkby.com/widget.js`}></script>)}
      </Helmet>

      <AdSetup
        predefineAds={pick(
          ads,
          article.article_type === 'games' ? [
            'game_adhesion',
            'standard_inarticle_video',
            // 'game_siderail1',
            // 'game_siderail2',
            // 'game_incontent1',
            // 'game_incontent2',
            // 'game_enterprise_billboard',
          ] :
            adConfig.layout === 'sponsored' ? [
              // Only the adhesion always renders, so it is the only slot predefined.
              // Everything else here is lazy and renders on scroll (or not at all on a
              // single-half article), so predefining put phantom slots in the initial
              // ad request for divs that did not exist:
              //   - sponsored_inarticle_native was referenced NOWHERE in the codebase
              //     and could never render (GAM: 0 impressions in 30 days).
              //   - sponsored_inarticle1/2 are <Ad lazy predefined> — contradictory:
              //     predefined requests them immediately, lazy withholds the div.
              //   - sponsored_siderail1/2 were predefined AND re-defined by <Ad> (which
              //     has no `predefined` prop), double-calling defineSlot on one div.
              // They all define themselves lazily, on render, now.
              'sponsored_adhesion',
            ] :
            [
              // Only slots that render near the top of virtually every article are
              // predefined, so the initial (single-request) ad call carries no phantom
              // slots. standard_inarticle_video and standard_inarticle_native are
              // deliberately absent: they sit late in chunkSeparatorOrder and do not
              // render on shorter articles, so predefining them requested ads for divs
              // that never existed. They now define themselves lazily, on render.
              'standard_high_impact',
              // 'standard_billboard', // commented out per request — the adStandardBillboard <Ad> is disabled in StandardArticleLayout, so don't predefine a slot for a div that no longer renders.
              'standard_adhesion',
            ]
        )}
        pageLevelTargeting={
          [
            { key: 'page_type', value: article.article_type === 'games' ? 'game' : 'article' },
            { key: 'page_path', value: article.path || 'N/A' },
            { key: 'page_category', value: article.article_type === 'games' ? 'game' : 'article' },
            ...(targeting.slug ? [{ key: 'slug', value: targeting.slug }] : []),
            ...(targeting.page_id ? [{ key: 'page_id', value: article.id.toString() }] : []),
            ...(targeting.section ? [{ key: 'section', value: targeting.section }] : []),
            ...(targeting.tags && targeting.tags.length ? [{ key: 'tags', value: targeting.tags }] : []),
            ...(targeting.categories && targeting.categories.length ? [{ key: 'categories', value: targeting.categories }] : []),
            ...(targeting.author ? [{ key: 'author', value: targeting.author }] : []),

          ]
        }
      />

      <Metadata
        {...article}
        brand={brand}
        socialMedia={socialMedia}
        faviconUrl={faviconUrl}
        amp={isAMPEnabled(article)}
        urlBreadcrumbs={article.url_bread_crumbs}
      />

      <Spacer maxWidth={article.article_type === 'games' ? layoutWidths.wideContentPane : layoutWidths.contentPane}>
        {!hideAds && adConfig.layout === 'standard' && ads.standard_high_impact && (
          <div className={styles.hiddenAd}>
            <Ad
              ad={ads.standard_high_impact}
              gutter={false}
              inline
              predefined
              backgroundColor={'transparent'}
            />
          </div>
        )}

        <Spacer space={'m'} selfH={'xs'} align={'center'}>
          {article.article_type === 'games' ?
            (
              <GameLayout
                ads={ads}
                article={article}
                hideComments={hideComments}
                hideStickyRecirc={hideStickyRecirc}
                hideAds={hideAds}
                showLinkbyPubFeed={showLinkbyPubFeed}
              />
            ) : (
              adConfig.layout === 'sponsored' ? (
                <SponsoredArticleLayout
                  ads={ads}
                  article={article}
                  hideAds={hideAds}
                  hideComments={hideComments}
                  showLinkbyPubFeed={showLinkbyPubFeed}
                />
              ) : 
              <StandardArticleLayout
                ads={ads}
                article={article}
                hideComments={hideComments}
                hideStickyRecirc={hideStickyRecirc}
                hideAds={hideAds}
                showLinkbyPubFeed={showLinkbyPubFeed}
              />
            )
          }
        </Spacer>

        {/* {adConfig.layout === 'standard' && (
          <ContinueReading
            recirculationAd1={ads.standard_recirculation_native1}
            recirculationAd2={ads.standard_recirculation_native2}
          />
        )} */}

        {buyoutIsExpired && (
          <Spacer 
            className={'ArticleHolidays'} 
            space={'l'} 
            maxWidth={article.article_type === 'games' ? layoutWidths.wideContentPane : layoutWidths.contentPane}
          >
            <LazyLoad>
              <ArticleHolidays />
            </LazyLoad>

            {!hideAds && adConfig.layout === 'standard' && (
              <Spacer align={'center'}>
                <Ad
                  ad={article.article_type === 'games' ?
                    ads.game_enterprise_billboard :
                    ads.standard_enterprise_billboard
                  }
                  backgroundColor={'transparent'}
                  initialSize={[300, 90]}
                  lazy
                />
              </Spacer>
            )}
          </Spacer>
        )}
      </Spacer>

      { // Show Typeform only if buyout is expired
      buyoutIsExpired && (
        <>
          {activeTypeform && (
            <TypeformEmbed
              popup
              autoOpen
              url={activeTypeform}
              hideHeaders
              hideFooter
            />
          )}
        </>
      )}

      { //  Show Taboola feed only if buyout is expired and layout is not sponsored
      buyoutIsExpired && adConfig.layout !== 'sponsored' && (
        <>
          {!hideAds && (
            <Spacer
              space={'m'}
              selfH={'m'}
              selfT={'m'}
              align={'center'}
              maxWidth={article.article_type === 'games' ? layoutWidths.wideContentPane : layoutWidths.contentPane}
              className={`taboola-feed`}
            >
              <div id="taboola-below-article-thumbnails"></div>
              <script
                type="text/javascript"
                dangerouslySetInnerHTML={{ __html: `
                  window._taboola = window._taboola || [];
                  _taboola.push({
                    mode: 'alternating-thumbnails-a',
                    container: 'taboola-below-article-thumbnails',
                    placement: 'Below Article Thumbnails',
                    target_type: 'mix'
                  });
                ` }}
              />
              <Ad
                className={cx(styles.recirculationAd, styles.taboolaFeedSplitAd)}
                ad={article.article_type === 'games' ?
                  ads.game_recirculation_native1 :
                  ads.standard_recirculation_native1
                }
                gutter={'s'}
                // backgroundColor="transparent"
                initialSize={['100%', 0]}
              />
              <Ad
                className={cx(styles.recirculationAd, styles.taboolaFeedSplitAd)}
                ad={article.article_type === 'games' ?
                  ads.game_recirculation_native2 :
                  ads.standard_recirculation_native2
                }
                gutter={'s'}
                // backgroundColor="transparent"
                initialSize={['100%', 0]}
              />
              <MembershipChallenge
                title="Want to read more stories like these?"
                classes={`${styles.taboolaFeedSplitAd}`}
                onClick={() => {
                  raiseEvent(SIGNUP_ARTICLE_COLLECTION_CHALLENGE);
                }}
              />
            </Spacer>
          )}
        </>
      )}
    </PageBody>
  );
};

const articleProps = memoize(
  articleSelector,
  socialMediaSelector,
  adsSelector,
  adConfigSelector,
  faviconSelector,
  suggestedArticlesUrlSelector,
  hideCommentsSelector,
  hideStickyRecircSelector,
  hideAdsSelector,
  showLinkbyPubFeedSelector,
  (
    article,
    socialMedia,
    ads,
    adConfig,
    faviconUrl,
    suggestedArticlesUrl,
    hideComments,
    hideStickyRecirc,
    hideAds,
    showLinkbyPubFeed
  ) => ({
    article,
    socialMedia,
    ads,
    adConfig,
    faviconUrl,
    suggestedArticlesUrl,
    hideComments,
    hideStickyRecirc,
    hideAds,
    showLinkbyPubFeed,
  })
);

const dispatchProps = dispatch => ({
  loadHolidays: url => dispatch(actions.loadHolidays(url)),
  loadSuggestedArticles: url => dispatch(actions.loadSuggestedArticles(url)),
});

const withOnMount = compose(
  withState('activeTypeform', 'setActiveTypeform', undefined),
  // withState('trackingProcessed', 'setTrackingHasBeenProcessed', false),
  withProps(
    ({
      suggestedArticlesUrl,
      loadSuggestedArticles,
      article,
      setActiveTypeform,
      // trackingProcessed,
      // setTrackingHasBeenProcessed,
    }) => ({
      onMount: () => {
        if (suggestedArticlesUrl) loadSuggestedArticles(suggestedArticlesUrl);
        window.addEventListener(
          'message',
          ({ data }) => {
            try {
              if (
                data.typeform &&
                data.typeform.includes('startsat60.typeform.com')
              ) {
                setActiveTypeform(data.typeform);
              }
            } catch (err) {
              // Do nothing. It was probably just a non-TypeForm message.
            }
          },
          false
        );

        // const trackUser = (email) => {
        //   if (!trackingProcessed && email !== undefined) {
        //     console.log('[Article onMount] Tracking', { email, article });
        //     const WAIT_BEFORE_TRACKING = 10000;
        //     const trackingTimer = setInterval(() => {
        //       const postTracking = async () => {
        //         post(endpoints.userTracking, {
        //           email,
        //           aggregator_url: article.link,
        //           aggregator_text: article.category,
        //         })
        //       };
  
        //       //  Run the tracker once and clear the interval
        //       postTracking()
        //       .finally(() => {
        //         clearInterval(trackingTimer);
        //         setTrackingHasBeenProcessed(true);
        //       })
        //     }, WAIT_BEFORE_TRACKING);
        //   };  
        // }
        
        // getTrackingEmail().then((email => trackUser(email)));

        pushEvent('travelPageView', {
          virtualPageUrl: location.href,
          pageCategory: 'Articles',
          pageSection: article.category,
          pageNumber: 1,
          pageTitle: article.slug,
        });
      },
    })
  ),
  callOnMount('onMount')
);

export default compose(
  connect(articleProps, dispatchProps),
  withOnMount,
  onlyUpdateForKeys(['activeTypeform']),
  styled(articleStyles)
)(Article);
